import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { ActionButton, SelectInput, TextInput, TextArea, Field } from "./common";

const parseWells = (raw) => raw.split(/[\n,]/).map((w) => w.trim()).filter(Boolean);

export const LocationPicker = ({ customerId, value, snapshotName, onChange, testId }) => {
  const [records, setRecords] = useState([]);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [wells, setWells] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let current = true;
    setAdding(false); setError("");
    if (!customerId) { setRecords([]); return () => { current = false; }; }
    api.get("/locations").then(({ data }) => {
      if (current) setRecords(data.filter((l) => l.customer_id === customerId));
    }).catch((e) => { if (current) setError(errMsg(e)); });
    return () => { current = false; };
  }, [customerId]);

  const options = records.map((l) => ({ value: l.id, label: l.wells?.length ? `${l.name} — ${l.wells.length} well${l.wells.length === 1 ? "" : "s"}` : l.name }));
  const selected = records.find((l) => l.id === value);

  const save = async () => {
    if (!name.trim()) { setError("Enter a location name."); return; }
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/locations", { customer_id: customerId, name: name.trim(), wells: parseWells(wells) });
      setRecords((prev) => [data, ...prev]);
      onChange(data);
      setAdding(false); setName(""); setWells("");
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid={`${testId}-location-field`} className="space-y-2">
      <label className="block text-xs font-mono uppercase tracking-[0.15em] text-slate-500">Location (well site)</label>
      <SelectInput testId={`${testId}-location-select`} value={value || ""} disabled={!customerId || busy}
        onChange={(e) => { const l = records.find((r) => r.id === e.target.value); onChange(l || null); }}
        placeholder={customerId ? "Select a location…" : "Select a customer first"} options={options} />
      {selected?.wells?.length > 0 && <p className="text-[11px] text-slate-500" data-testid={`${testId}-location-wells`}>Wells: {selected.wells.join(", ")}</p>}
      {!value && snapshotName && <p className="text-xs text-slate-500" data-testid={`${testId}-location-legacy`}>Saved ticket location: {snapshotName}</p>}
      {customerId && !adding && <ActionButton type="button" testId={`${testId}-location-add`} variant="secondary" onClick={() => { setAdding(true); setError(""); }} className="!text-xs min-h-11"><Plus size={14} /> Add New Location</ActionButton>}
      {adding && (
        <div className="border border-slate-700 rounded-md p-3 space-y-2" data-testid={`${testId}-location-add-panel`}>
          <Field label="Location name"><TextInput testId={`${testId}-location-name`} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. SOUTH MAN" /></Field>
          <Field label="Wells (one per line or comma-separated)"><TextArea testId={`${testId}-location-wells-input`} rows={3} value={wells} onChange={(e) => setWells(e.target.value)} placeholder={"36-1-12HC 003 ALT\n36-1-12HC 001 ALT\n36-1-12HC 002 ALT"} /></Field>
          <div className="flex gap-2">
            <ActionButton type="button" testId={`${testId}-location-save`} onClick={save} disabled={busy} className="!text-xs">{busy ? "Saving…" : "Save Location"}</ActionButton>
            <ActionButton type="button" variant="secondary" testId={`${testId}-location-cancel`} onClick={() => { setAdding(false); setError(""); }} className="!text-xs">Cancel</ActionButton>
          </div>
        </div>
      )}
      {error && <p role="alert" data-testid={`${testId}-location-error`} className="text-xs text-red-500">{error}</p>}
    </div>
  );
};
